import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { loginSchema, LoginFormValues } from './schemas'
import { useAuth } from '@/context/auth-context'

function LoginForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Use Auth Context instead of Redux
  const { signIn } = useAuth()

  // Get the return path from location state, or default to '/'
  const from = location.state?.from?.pathname || '/'

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: LoginFormValues) => {
    try {
      setIsLoading(true)
      setError(null)

      // Use Auth Context for login with object parameter
      const { error: authError } = await signIn({ email: data.email, password: data.password })

      if (authError) {
        throw new Error(authError)
      }

      // Navigate after successful login
      navigate(from, { replace: true })
    } catch (err) {
      console.error('Login error:', err)

      // Handle specific error messages
      let errorMessage = 'Failed to sign in'

      if (typeof err === 'string') {
        // If the error is already a string
        errorMessage = err
      } else if (err instanceof Error) {
        // If it's an Error object
        errorMessage = err.message
      }

      // Check for specific error patterns
      if (errorMessage.includes('Invalid login credentials')) {
        errorMessage = 'Incorrect email or password. Please try again.'
      } else if (errorMessage.includes('rate limit')) {
        errorMessage = 'Too many login attempts. Please try again later.'
      } else if (errorMessage.toLowerCase().includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleQuickLogin = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      setError(null)

      // Set form values for quick login
      setValue('email', email)
      setValue('password', password)

      // Use Auth Context for login with object parameter
      const { error: authError } = await signIn({ email, password })

      if (authError) {
        throw new Error(authError)
      }

      // Navigate after successful login
      navigate(from, { replace: true })
    } catch (err) {
      console.error('Quick login error:', err)

      // Handle specific error messages
      let errorMessage = 'Failed to sign in with quick login'

      if (typeof err === 'string') {
        // If the error is already a string
        errorMessage = err
      } else if (err instanceof Error) {
        // If it's an Error object
        errorMessage = err.message
      }

      // Check for specific error patterns
      if (errorMessage.includes('Invalid login credentials')) {
        errorMessage = 'Incorrect email or password. Please try again.'
      } else if (errorMessage.includes('rate limit')) {
        errorMessage = 'Too many login attempts. Please try again later.'
      } else if (errorMessage.toLowerCase().includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const quickLoginUsers = [
    { name: 'Arris138', email: 'Arris138@gmail.com', password: 'Test1234' },
    { name: 'Player2', email: 'player2@gmail.com', password: '11111111A' },
    { name: 'Player3', email: 'player3@gmail.com', password: '11111111A' },
    { name: 'Player4', email: 'player4@gmail.com', password: '11111111A' },
  ]

  return (
    <div className="w-full max-w-md mx-auto p-6 space-y-6 bg-background rounded-xl shadow-lg">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-text">Welcome Back</h1>
        <p className="text-text/70 mt-2">Sign in to your account to continue</p>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-500 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-text">
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="you@example.com"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('email')}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="text-sm font-medium text-text">
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-xs text-primary hover:underline"
              tabIndex={-1}
            >
              Forgot password?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            placeholder="••••••••"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('password')}
          />
          {errors.password && (
            <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-700"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-background text-text/70">Quick Logins</span>
        </div>
      </div>

      <div className="space-y-2">
        {quickLoginUsers.map(user => (
          <button
            key={user.email}
            type="button"
            onClick={() => handleQuickLogin(user.email, user.password)}
            disabled={isLoading}
            className="w-full py-2 px-4 bg-accent/90 hover:bg-accent text-black font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing in...' : `Quick Login (${user.name})`}
          </button>
        ))}
      </div>

      <div className="text-center text-sm text-text/70">
        Don't have an account?{' '}
        <Link to="/signup" className="text-primary hover:underline">
          Sign up
        </Link>
      </div>
    </div>
  )
}

export { LoginForm }
